import React, { useState, useEffect } from 'react';
import { Card, Table, Form, Button, Space, DatePicker, Input, message, Tag, Tooltip } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import ClientSelectWrapper from './ClientSelectWrapper';
import HubSelectWrapper from './HubSelectWrapper';
import {
    GET_CLIENT_HUB_ZONE_LISTING,
    GetClientHubZoneListingResponse,
    GetClientHubZoneListingVariables,
    ClientHubZoneType
} from '../graphql/queries/getClientHubZoneListing';
import { useTranslation } from 'react-i18next';

dayjs.extend(utc);

const { RangePicker } = DatePicker;

const ConfigListTab: React.FC = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [filters, setFilters] = useState<any>({});

    const { data, loading, refetch } = useQuery<
        GetClientHubZoneListingResponse,
        GetClientHubZoneListingVariables
    >(GET_CLIENT_HUB_ZONE_LISTING, {
        variables: {
            filters,
            pageRequest: {
                page: pagination.current,
                size: pagination.pageSize,
                sortBy: 'id',
                sortDirection: 'DESC'
            }
        },
        fetchPolicy: 'network-only'
    });

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const newFilters: any = {};

        if (values.channelId) newFilters.channelId = values.channelId;
        if (values.hubId) newFilters.hubId = values.hubId;
        if (values.zipcode) newFilters.zipcode = values.zipcode.trim();
        if (values.zone) newFilters.zone = values.zone.trim();
        if (values.activeTime && values.activeTime.length === 2) {
            // 用户选择的时间已经是UTC+0时间，直接转换为ISO格式
            newFilters.activeTime = {
                start: dayjs.utc(values.activeTime[0]).format(),
                end: dayjs.utc(values.activeTime[1]).format()
            };
        }

        const newPagination = { ...pagination, current: 1 };
        setFilters(newFilters);
        setPagination(newPagination);
        
        // 强制刷新数据，即使查询条件没有变化
        // 直接调用 refetch 并传入新的 variables，确保总是重新请求
        refetch({
            filters: newFilters,
            pageRequest: {
                page: 1,
                size: newPagination.pageSize,
                sortBy: 'id',
                sortDirection: 'DESC'
            }
        });
    };

    const handleReset = () => {
        form.resetFields();
        setFilters({});
        setPagination({ current: 1, pageSize: 20 });
    };

    const handleTableChange = (newPagination: any) => {
        setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize
        });
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: '客户',
            dataIndex: 'channelId',
            key: 'channelId',
            width: 180,
            render: (channelId: number, record: ClientHubZoneType) => (
                <Tag color="blue">
                    {record.channelName || channelId}
                    {record.channelName && <span className="ml-1 text-xs text-gray-500">(ID: {channelId})</span>}
                </Tag>
            )
        },
        {
            title: 'Hub',
            dataIndex: 'hubId',
            key: 'hubId',
            width: 180,
            render: (hubId: number, record: ClientHubZoneType) => (
                <Tag color="cyan">
                    {record.hubName || hubId}
                    {record.hubName && <span className="ml-1 text-xs text-gray-500">(ID: {hubId})</span>}
                </Tag>
            )
        },
        {
            title: '邮编',
            dataIndex: 'zipcode',
            key: 'zipcode',
            width: 100,
            render: (zipcode: string) => <span className="font-mono">{zipcode}</span>
        },
        {
            title: 'Zone区',
            dataIndex: 'zone',
            key: 'zone',
            width: 120,
            render: (zone: string) => <Tag color="green">{zone}</Tag>
        },
        {
            title: '生效时间（UTC+0）',
            dataIndex: 'activeTime',
            key: 'activeTime',
            width: 200,
            render: (timestamp: number | null) => timestamp ? dayjs.unix(timestamp).utc().format('YYYY-MM-DD HH:mm:ss') : '-'
        },
        {
            title: '操作员',
            dataIndex: 'operatorId',
            key: 'operatorId',
            width: 100,
            render: (operatorId: number) => <Tag>ID: {operatorId}</Tag>
        },
        {
            title: '创建时间（UTC+0）',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: (timestamp: number) => dayjs.unix(timestamp).utc().format('YYYY-MM-DD HH:mm:ss')
        }
    ];

    const listingData = data?.getClientHubZoneListing;

    return (
        <div className="p-6">
            <Card className="mb-4">
                <Form form={form} layout="vertical">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Form.Item label="客户" name="channelId">
                            <ClientSelectWrapper placeholder="选择客户" />
                        </Form.Item>
                        <Form.Item label="Hub" name="hubId">
                            <HubSelectWrapper placeholder="选择Hub" />
                        </Form.Item>
                        <Form.Item label="邮编" name="zipcode">
                            <Input placeholder="输入邮编" allowClear />
                        </Form.Item>
                        <Form.Item label="Zone区" name="zone">
                            <Input placeholder="输入Zone区（模糊查询）" allowClear />
                        </Form.Item>
                        <Form.Item 
                            label={
                                <span>
                                    生效时间范围
                                    <span className="ml-2 text-orange-600 font-semibold text-xs">（UTC+0）</span>
                                </span>
                            }
                            name="activeTime"
                            extra={
                                <span className="text-gray-500 text-xs">
                                    当前 UTC+0 时间：{dayjs.utc().format('YYYY-MM-DD HH:mm:ss')} UTC+0
                                </span>
                            }
                        >
                            <RangePicker
                                showTime
                                className="w-full"
                                format="YYYY-MM-DD HH:mm"
                                placeholder={['开始时间（UTC+0）', '结束时间（UTC+0）']}
                                value={form.getFieldValue('activeTime') ? [
                                    form.getFieldValue('activeTime')[0] ? dayjs.utc(form.getFieldValue('activeTime')[0]) : null,
                                    form.getFieldValue('activeTime')[1] ? dayjs.utc(form.getFieldValue('activeTime')[1]) : null
                                ] : undefined}
                                onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                        // 将选择的日期转换为UTC时间
                                        form.setFieldsValue({
                                            activeTime: [
                                                dayjs.utc(dates[0].format('YYYY-MM-DD HH:mm:ss')),
                                                dayjs.utc(dates[1].format('YYYY-MM-DD HH:mm:ss'))
                                            ]
                                        });
                                    } else {
                                        form.setFieldsValue({ activeTime: null });
                                    }
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button onClick={handleReset}>
                            重置
                        </Button>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                        >
                            查询
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => refetch()}
                        >
                            刷新
                        </Button>
                    </div>
                </Form>
            </Card>

            <Card
                title={
                    <div className="flex items-center justify-between">
                        <span className="text-lg font-semibold">
                            配置列表
                            {listingData && (
                                <span className="ml-2 text-sm text-gray-500 font-normal">
                                    共 {listingData.totalElements} 条记录
                                </span>
                            )}
                        </span>
                    </div>
                }
            >
                <Table
                    columns={columns}
                    dataSource={listingData?.content || []}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: listingData?.totalElements || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => t('table.pagination.totalItems', { total }),
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 1200 }}
                    locale={{
                        emptyText: (
                            <div className="py-8">
                                <div className="text-gray-400 text-lg mb-2">暂无数据</div>
                                <div className="text-gray-400 text-sm">请调整筛选条件或导入新配置</div>
                            </div>
                        )
                    }}
                />
            </Card>
        </div>
    );
};

export default ConfigListTab;

